import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import apiConfig from './pages/credentials/apiConfig';
import SubscriptionModal from './pages/credentials/SubscriptionModal';
import STRIPE_PUBLIC_KEY from './stripeConfig';

const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

// Create Subscription Context
const SubscriptionContext = createContext(null);

export const SubscriptionProvider = ({ children }) => {
  const [hasSubscription, setHasSubscription] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [userData, setUserData] = useState(null);
  const [subscriptionSource, setSubscriptionSource] = useState(''); // 'signup', 'login', 'expired'
  
  const navigate = useNavigate();
  const location = useLocation();

  // Check subscription status function
const checkSubscriptionStatus = async () => {
  const userId = localStorage.getItem("userId");
  const authToken = localStorage.getItem("authToken");
  
  if (!userId || !authToken) {
    setIsLoading(false);
    return false;
  }
  
  setIsLoading(true);
  
  try {
    const response = await axios.get(
      `${apiConfig.apiBaseURL}/getUserProfile/${userId}`,
      {
        headers: {
          "x-api-key": apiConfig["x-api-key"],
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authToken}`
        }
      }
    );

    if (response.data && response.data.success && response.data.user) {
      const user = response.data.user;
      setUserData(user);
      
      
      // Check if user has valid subscription
      // Updated check that handles both possible property names
      const hasActiveSubscription = !!user.stripeSubscriptionId && user.stripeSubscriptionStatus === 'active'
    
      
      setHasSubscription(hasActiveSubscription);
      
      // Determine if we need to show subscription modal
      if (!hasActiveSubscription) {
        // Determine source of subscription need
        if (location.pathname === '/profile' || location.pathname === '/create-profile') {
          setSubscriptionSource('signup');
        } else {
          setSubscriptionSource('login');
        }
        
        setShowSubscriptionModal(true);
      } else {
        // Explicitly hide the modal when subscription is detected
        setShowSubscriptionModal(false);
      }
      
      setIsLoading(false);
      return hasActiveSubscription;
    }
  } catch (error) {
    console.error("Error checking subscription status:", error);
    setIsLoading(false);
    return false;
  }
};

  // Check subscription status when component mounts or auth changes
  useEffect(() => {
    checkSubscriptionStatus();
  }, []);

  // Handle subscription success
  const handleSubscriptionSuccess = () => {
    setHasSubscription(true);
    setShowSubscriptionModal(false);
  };
  
  // Close subscription modal - different behavior based on source
  const handleCloseModal = () => {
    // If user closes without subscribing during signup, log them out
    if (subscriptionSource === 'signup' && !hasSubscription) {
      // Logout user
      localStorage.removeItem("authToken");
      localStorage.removeItem("userId");
      localStorage.removeItem("userProfile");
      localStorage.removeItem("isLoggedIn");
      sessionStorage.removeItem("authToken");
      localStorage.removeItem("loginResponseId");
      localStorage.setItem('hasLoggedOut', 'true');
      
      // Notify other tabs about logout
      localStorage.setItem('logoutEvent', Date.now().toString());
      setTimeout(() => localStorage.removeItem('logoutEvent'), 500);
      
      // Navigate to login
      navigate('/');
    } else {
      // For other sources, just close the modal
      setShowSubscriptionModal(false);
    }
  };

  // Manual subscription check - can be called from components
  const checkSubscription = async () => {
    return await checkSubscriptionStatus();
  };

  return (
    <SubscriptionContext.Provider 
      value={{ 
        hasSubscription, 
        isLoading, 
        userData,
        checkSubscription,
        setShowSubscriptionModal
      }}
    >
      {children}
      
      {/* Subscription Modal */}
      <Elements stripe={stripePromise}>
        <SubscriptionModal
          show={showSubscriptionModal}
          handleClose={handleCloseModal}
          onSubscriptionSuccess={handleSubscriptionSuccess}
          subscriptionSource={subscriptionSource}
          currentPath={location.pathname}
        />
      </Elements>
    </SubscriptionContext.Provider>
  );
};

// Custom hook to use the subscription context
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === null) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};